/**
 * Configuration helper to handle environment variables
 */

export const getApiBaseUrl = (): string => {
  // Use environment variable if available (set during build)
  const envApiUrl = import.meta.env.VITE_API_BASE_URL;
  
  if (envApiUrl) {
    return envApiUrl;
  }
  
  // Default fallback for local development
  return 'http://localhost:3001';
};

/**
 * Get Cognito User Pool ID from environment variables
 */
export const getCognitoUserPoolId = (): string => {
  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
  
  if (!userPoolId) {
    throw new Error('VITE_COGNITO_USER_POOL_ID environment variable is not set');
  }
  
  return userPoolId;
};

/**
 * Get Cognito User Pool Client ID from environment variables
 */
export const getCognitoClientId = (): string => {
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
  
  if (!clientId) {
    throw new Error('VITE_COGNITO_CLIENT_ID environment variable is not set');
  }
  
  return clientId;
};

/**
 * Get AWS Region for Cognito from environment variables
 */
export const getCognitoRegion = (): string => {
  const region = import.meta.env.VITE_AWS_REGION;

  // Default to us-east-1 if not specified
  return region || 'us-east-1';
};

/**
 * Whether "Sign in with Microsoft" is available in this environment.
 *
 * Mirrors the CloudFormation `HasAzureSso` condition (surfaced as the SsoEnabled
 * stack output). An environment deployed without the Entra secret has no
 * identity provider configured, so showing the button there would send people to
 * a Cognito error page.
 */
export const isSsoEnabled = (): boolean =>
  import.meta.env.VITE_SSO_ENABLED === 'true' && Boolean(import.meta.env.VITE_COGNITO_DOMAIN);

/**
 * Get the Cognito hosted-UI origin that brokers Entra sign-in
 * (https://<prefix>.auth.<region>.amazoncognito.com), without a trailing slash.
 */
export const getCognitoDomain = (): string => {
  const domain = import.meta.env.VITE_COGNITO_DOMAIN;

  if (!domain) {
    throw new Error('VITE_COGNITO_DOMAIN environment variable is not set');
  }

  return domain.replace(/\/+$/, '');
};

/**
 * Path the Cognito hosted UI redirects back to after Entra sign-in.
 *
 * Must stay in sync with the `CallbackURLs` of the user pool client in
 * template.yaml and with the route registered in App.tsx.
 */
export const SSO_CALLBACK_PATH = '/auth/callback';

/** Absolute redirect URI for this origin. */
export const getSsoRedirectUri = (): string =>
  `${window.location.origin}${SSO_CALLBACK_PATH}`;

/**
 * Base URL of the Delivery Central UI for this environment, used to deep-link a pushed
 * project. Optional: when unset the push status still renders, just without the link.
 * Never defaulted to a hard-coded environment — a QA build linking to production
 * projects would be worse than no link.
 */
export const getDeliveryCentralBaseUrl = (): string | undefined =>
  import.meta.env.VITE_DELIVERY_CENTRAL_BASE_URL || undefined;
